/**
 * ThemeToggle Component (AC-846)
 *
 * A 3-way segmented control for switching between Light, Dark, and System themes.
 * Reads from and writes to the theme store (useThemeStore).
 *
 * Technical Design Reference: docs/technical/TDD-AC-843-dark-mode.md
 *
 * Features:
 * - Three options: Light (Sun icon), Dark (Moon icon), System (Monitor icon)
 * - Active option highlighted with primary brand color
 * - Accessible: role="radiogroup", role="radio", aria-checked, keyboard navigation
 * - Immediate theme change on click (no page reload)
 *
 * Parent: SettingsPage > General tab > Appearance card
 * User Access: Login > Sidebar Settings > General > Appearance
 */

import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useThemeStore } from '../../store/themeStore';
import type { ThemePreference } from '../../store/themeStore';

interface ThemeToggleProps {
  /** Additional CSS classes for the wrapper */
  className?: string;
}

const options: Array<{
  value: ThemePreference;
  label: string;
  Icon: React.FC<{ className?: string }>;
}> = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

const ThemeToggle: React.FC<ThemeToggleProps> = ({ className = '' }) => {
  const { preference, setPreference } = useThemeStore();

  const handleKeyDown = (e: React.KeyboardEvent, value: ThemePreference) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setPreference(value);
      return;
    }

    const currentIndex = options.findIndex((opt) => opt.value === value);
    let nextIndex = -1;

    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % options.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + options.length) % options.length;
    }

    if (nextIndex >= 0) {
      setPreference(options[nextIndex].value);
      // Focus the next option
      const radiogroup = (e.target as HTMLElement).closest('[role="radiogroup"]');
      const nextButton = radiogroup?.querySelectorAll('[role="radio"]')[nextIndex] as HTMLElement;
      nextButton?.focus();
    }
  };

  return (
    <div className={className}>
      <div
        role="radiogroup"
        aria-label="Theme preference"
        className="inline-flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1"
      >
        {options.map((option) => {
          const isActive = preference === option.value;
          return (
            <button
              key={option.value}
              role="radio"
              aria-checked={isActive ? 'true' : 'false'}
              aria-label={option.label}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setPreference(option.value)}
              onKeyDown={(e) => handleKeyDown(e, option.value)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white dark:hover:text-white'
              }`}
            >
              <option.Icon className="w-4 h-4" />
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ThemeToggle;
